import { API_ENDPOINTS } from '../../config/api-config';
import axios from '../axios-config';

/**
 * Polls the mint status endpoint until the job completes or fails
 * @param {string} jobID - The job ID to check
 * @param {number} interval - Polling interval in milliseconds (default: 2000ms)
 * @param {number} maxAttempts - Maximum number of polling attempts (default: 90 - 3 minutes total)
 * @returns {Promise<{success: boolean, state?: string, error?: string}>}
 */
export const checkMintStatus = async (
  jobID,
  interval = 4000,
  maxAttempts = 180
) => {
  if (!jobID) {
    return { success: false, error: 'No job ID provided' };
  }

  let attempts = 0;

  return new Promise((resolve) => {
    const checkStatus = async () => {
      try {
        attempts++;

        // Make sure to fix the API endpoint to return the string

        const response = await axios.get(
          API_ENDPOINTS.CHECK.MINT_STATUS(jobID)
        );

        const status = response.data.state;

        if (status === 'completed' || status === 'FINALIZED') {
          resolve({ success: true, state: 'completed' });
          return;
        } else if (
          status === 'active' ||
          status === 'PENDING' ||
          status === 'waiting' ||
          status === 'delayed' ||
          status === 'DELAYED'
        ) {
          // Continue polling if under max attempts
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, interval);
          } else {
            resolve({
              success: false,
              state: 'timeout',
              error: 'Max polling attempts reached',
            });
          }
        } else {
          // Any other status is considered a failure
          resolve({
            success: false,
            state: status || 'failed',
            error: response.data.message || 'Minting process failed',
          });
        }
      } catch (error) {
        // If the error response contains a failed state, resolve immediately
        if (
          error.response &&
          error.response.data &&
          error.response.data.state === 'failed'
        ) {
          resolve({
            success: false,
            state: 'failed',
            error: error.response.data.message || 'Minting process failed',
          });
          return;
        }

        // Immediately fail on network errors
        if (error.code === 'ERR_NETWORK') {
          resolve({
            success: false,
            state: 'network_error',
            error: error.message || 'Network error while checking mint status',
          });
          return;
        }

        // Continue polling on other errors if under max attempts
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, interval);
        } else {
          resolve({
            success: false,
            state: 'error',
            error: error.message || 'Error checking mint status',
          });
        }
      }
    };

    // Start checking
    checkStatus();
  });
};

/**
 * Hook that polls for mint status and provides a progress percentage
 * For use with useEffect in components
 * @param {string} jobID - The job ID to check
 * @param {function} onStatusChange - Callback when status changes
 * @param {function} onComplete - Callback when complete (success or failure)
 * @param {number} interval - Polling interval in milliseconds
 * @returns {function} Cleanup function to cancel polling
 */
export const useMintStatusPolling = (
  jobID,
  onStatusChange,
  onComplete,
  interval = 2000
) => {
  let isCancelled = false;
  let attempts = 0;
  const maxAttempts = 180; // 3 minutes

  const checkStatus = async () => {
    if (isCancelled || !jobID) return;

    try {
      attempts++;
      const progress = Math.min(Math.floor((attempts / maxAttempts) * 100), 95);

      const response = await axios.get(API_ENDPOINTS.CHECK.MINT_STATUS(jobID));
      const status = response.data.state;

      // Call the status change callback
      onStatusChange?.({
        status,
        progress,
        attempt: attempts,
        maxAttempts,
      });

      if (status === 'completed' || status === 'FINALIZED') {
        onComplete?.({ success: true, state: 'completed' });
      } else if (
        status === 'active' ||
        status === 'PENDING' ||
        status === 'waiting' ||
        status === 'delayed' ||
        status === 'DELAYED'
      ) {
        // Continue polling if under max attempts
        if (attempts < maxAttempts && !isCancelled) {
          setTimeout(checkStatus, interval);
        } else if (!isCancelled) {
          onComplete?.({
            success: false,
            state: 'timeout',
            error: 'Max polling attempts reached',
          });
        }
      } else {
        // Any other status is considered a failure
        onComplete?.({
          success: false,
          state: status || 'failed',
          error: response.data.message || 'Minting process failed',
        });
      }
    } catch (error) {
      console.error('Error checking mint status:', error);

      // Immediately fail on network errors
      if (error.code === 'ERR_NETWORK') {
        onComplete?.({
          success: false,
          state: 'network_error',
          error: error.message || 'Network error while checking mint status',
        });
        return;
      }

      // Continue polling on other errors if under max attempts
      if (attempts < maxAttempts && !isCancelled) {
        setTimeout(checkStatus, interval);
      } else if (!isCancelled) {
        onComplete?.({
          success: false,
          state: 'error',
          error: error.message || 'Error checking mint status',
        });
      }
    }
  };

  // Start checking
  checkStatus();

  // Return cleanup function
  return () => {
    isCancelled = true;
  };
};

export default checkMintStatus;
